// ============================================================================
// ProbSolver v3.0 — Report Hooks (Team-Scoped)
// ============================================================================
import { useQuery } from "@tanstack/react-query";
import api from "@services/api";
import { useTeamContext } from "./useTeamContext";

export function use6DReport() {
  const { teamQueryKey } = useTeamContext();
  return useQuery({
    queryKey: [...teamQueryKey, "report", "6d"],
    queryFn: async () => {
      const res = await api.get("/stats/report");
      return res.data.data.report;
    },
    staleTime: 1000 * 60 * 5,
  });
}

// AI-generated readiness verdict — independent query so /report scores
// render immediately and the verdict card loads progressively. Server
// caches via VerdictLog (5 min TTL on identical evidence), so calling
// this hook on every report page load is cheap after the first hit.
export function useReadinessVerdict() {
  const { teamQueryKey } = useTeamContext();
  return useQuery({
    queryKey: [...teamQueryKey, "report", "verdict"],
    queryFn: async () => {
      const res = await api.get("/stats/verdict");
      return res.data.data;
    },
    // Match server's cache window. Refetching sooner just hits the
    // cached VerdictLog, so 5 min is the natural floor.
    staleTime: 1000 * 60 * 5,
    // Don't retry the LLM call — server fallback already guarantees
    // a well-formed response, so a "failure" here is a real error
    // (auth, network) that a retry won't fix.
    retry: 0,
  });
}

export function usePersonalStats() {
  const { teamQueryKey } = useTeamContext();
  return useQuery({
    queryKey: [...teamQueryKey, "stats", "personal"],
    queryFn: async () => {
      const res = await api.get("/stats/personal");
      return res.data.data.stats;
    },
    staleTime: 1000 * 60 * 2,
  });
}

export function useLeaderboard() {
  const { teamQueryKey } = useTeamContext();
  return useQuery({
    queryKey: [...teamQueryKey, "leaderboard"],
    queryFn: async () => {
      const res = await api.get("/stats/leaderboard");
      return res.data.data.leaderboard;
    },
    staleTime: 1000 * 60 * 5,
  });
}

// NEW: Combined dashboard data hook
// Fetches personal stats and 6D report in parallel.
// Single coordinated loading state for the dashboard.
// Avoids the race condition in the old dashboard where
// recommendations were checked before data loaded.
export function useDashboardData() {
  const { teamQueryKey } = useTeamContext();

  const statsQuery = useQuery({
    queryKey: [...teamQueryKey, "stats", "personal"],
    queryFn: async () => {
      const res = await api.get("/stats/personal");
      return res.data.data.stats;
    },
    staleTime: 1000 * 60 * 2,
  });

  const reportQuery = useQuery({
    queryKey: [...teamQueryKey, "report", "6d"],
    queryFn: async () => {
      const res = await api.get("/stats/report");
      return res.data.data.report;
    },
    staleTime: 1000 * 60 * 5,
  });

  return {
    stats: statsQuery.data,
    report: reportQuery.data,
    isLoading: statsQuery.isLoading || reportQuery.isLoading,
    isError: statsQuery.isError || reportQuery.isError,
  };
}

// NEW: Team activity feed hook
export function useTeamActivity() {
  const { teamQueryKey } = useTeamContext();
  return useQuery({
    queryKey: [...teamQueryKey, "activity"],
    queryFn: async () => {
      const res = await api.get("/stats/activity");
      return res.data.data;
    },
    staleTime: 1000 * 60 * 2,
    // Activity feed is team-only — in personal mode
    // this gracefully returns empty since requireTeamContext
    // will still work with a personal team
  });
}

// NEW: Team analytics data hook
// Fetches product health scoped to the current team.
// The backend already handles team-scoping based on req.teamId.
// Separate from leaderboard — each has its own cache key and stale time.
export function useTeamAnalytics(period = 30) {
  const { teamQueryKey } = useTeamContext();
  return useQuery({
    queryKey: [...teamQueryKey, "analytics", period],
    queryFn: async () => {
      const res = await api.get(`/admin/product-health?period=${period}`);
      return res.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 min — analytics data doesn't need real-time freshness
  });
}

// NEW: Skill profile hook
export function useSkillProfile() {
    const { teamQueryKey } = useTeamContext()
    return useQuery({
        queryKey: [...teamQueryKey, 'skills'],
        queryFn: async () => {
            const res = await api.get('/stats/skills')
            return res.data.data.skills || []
        },
        staleTime: 1000 * 60 * 5, // 5 min — skill scores change with activity
    })
}
